<template>
  <div
    v-if="loading"
    class="h-[34px] w-full items-center justify-center bg-transparent"
  >
    <loader :is-loading="true" background-color="transparent" />
  </div>
  <div v-else class="flex w-full min-w-62-5 flex-col gap-y-4 py-4">
    <div
      v-for="result in modelValue"
      :key="result.id"
      class="cursor-pointer space-y-2"
      @click="showModal(result)"
    >
      <div class="flex items-center justify-between">
        <span class="font-semibold text-headline">
          {{ validateString(result.score_card?.name) }}
        </span>
        <span class="font-semibold text-headline">
          {{ result.points }}/{{
            $t("SCORECARDS.PTS", { points: result.score_card?.points || 0 })
          }}
        </span>
      </div>
      <scorecard-results-points-bar
        :categories="result.categories"
        :total="result.score_card?.points || 0"
        :knocked-out="isKnockoutTriggered(result)"
        started
        small
      />
    </div>
    <lf-badge v-if="!modelValue?.length" is-rounded class="font-semibold">
      <span class="px-4">-</span>
    </lf-badge>
  </div>
  <scorecard-preview-modal
    v-if="!!activeScorecard && showPreviewModal"
    :model="activeScorecard"
    :started="!!activeScorecard?.results?.length"
    :close="closePreviewModal"
  />
</template>
<script setup lang="ts">
import { type PropType, inject, provide, ref } from "vue";

import { convertToArray, validateString } from "@/helpers/common";
import { PlacementFunderKey, ScorecardsIsViewKey } from "@/helpers/symbols";
import { usePromiseWrapper } from "@/hooks/common";
import fundersApiService from "@/services/modules/funders";

import ScorecardPreviewModal from "@/components/scorecards/ScorecardPreviewModal.vue";
import ScorecardResultsPointsBar from "@/components/scorecards/ScorecardResultsPointsBar.vue";
import LfBadge from "@/components/ui/LfBadge.vue";

import type { IFunder } from "@/models/funders";
import type { IScorecard, IScorecardResult } from "@/models/scorecards";

defineProps({
  modelValue: {
    type: Array as PropType<IScorecardResult[]>,
  },
});

provide(ScorecardsIsViewKey, false);

const placementFunder = inject(PlacementFunderKey);

const isKnockoutTriggered = ({ knocked_out_at, knockouts }: IScorecardResult) =>
  !!knocked_out_at && !!knockouts?.length;

const showPreviewModal = ref(false);
const activeScorecard = ref<IScorecard | null>(null);

const showModal = async (result: IScorecardResult) => {
  activeScorecard.value = await getActiveResult(result);
  showPreviewModal.value = true;
};

const getScorecardFunder = async (
  result: IScorecardResult
): Promise<IFunder | null> => {
  const resultFunder = convertToArray(result.score_card?.funder)[0];

  if (!resultFunder?.id) {
    return null;
  }

  if (placementFunder?.id === resultFunder.id) {
    return placementFunder;
  }

  return (await getFunder(resultFunder.id)) ?? null;
};

const { fetchWrapper: getFunder, loading } = usePromiseWrapper(
  async (funderId: string | undefined) => {
    if (!funderId) {
      return;
    }
    return await fundersApiService.getFunder(funderId);
  },
  {
    onError: () => {
      showPreviewModal.value = false;
    },
  }
);

const getActiveResult = async (
  result: IScorecardResult
): Promise<IScorecard> => {
  const resultFunder = convertToArray(result.score_card?.funder)[0];
  return {
    borrower_type: result.score_card?.borrower_type || null,
    client_id: result.score_card?.client_id || "",
    client_name: result.score_card?.client_name || "",
    content: { version: "", categories: result.categories },
    created_at: result.score_card?.created_at || "",
    description: result.score_card?.description || "",
    id: result.score_card?.id || "",
    name: result.score_card?.name || "",
    points: result.score_card?.points || null,
    product_type_id: result.score_card?.product_type_id || null,
    results: [result],
    risk_tiers: convertToArray(result.score_card_risk_tier),
    runs_count: result.score_card?.runs_count || 0,
    status: result.score_card?.status || null,
    statuses: convertToArray(result.score_card_status),
    type: result.score_card?.type || null,
    user: result.user,
    latest_activity: null,
    funder_id: resultFunder?.id,
    funder: await getScorecardFunder(result),
    workflow_template: result.score_card?.workflow_template || null,
  };
};

const closePreviewModal = () => {
  showPreviewModal.value = false;
  activeScorecard.value = null;
};
</script>
